/* eslint-disable */
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

// --------------------
// INTERFACES
// --------------------
export interface AttendanceData {
  records: AttendanceRecord[];
  pagination: Pagination;
}

export interface AttendanceRecord {
  id: string;
  activityId: string;
  date: string;
  user: AttendanceUser;
  punchInTime: string;
  punchOutTime: string | null;
  breakTime: string;
  isHalfDay: boolean;
  isFullDay: boolean;
  isLate:boolean;
  overtime: string;
  totalProduction: string;
  status: string;
  notes?: string;
  sessionStatus: string;
  source: string;
  sessionNumber: number;
  lateMinutes:string
}

export interface AttendanceUser {
  id: string;
  name: string;
  email: string;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AttendanceApiResponse {
  success: boolean;
  data: AttendanceData;
}

// --------------------
// API FUNCTION
// --------------------
const getAttendanceEmployee = async (
  page: number = 1,
  limit: number = 10,
  filterType: string = "Today",
  startDate?: string | null,
  endDate?: string | null,
  userId?: string
): Promise<AttendanceApiResponse> => {
  const queryParams = new URLSearchParams();

  // Pagination
  queryParams.append("page", page.toString());
  queryParams.append("limit", limit.toString());

  // Filters
  queryParams.append("filterType", filterType); // e.g. Today, Last 7 Days, Custom Range
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  if (userId) queryParams.append("userId", userId);

  const { data } = await customAxios.get<AttendanceApiResponse>(
    `/attendance/dashboard/table?${queryParams.toString()}`
  );

  return data;
};

// --------------------
// REACT QUERY HOOK
// --------------------
export const useGetAttendanceEmployee = (
  page: number = 1,
  limit: number = 10,
  filterType: string = "Today",
  startDate?: string | null,
  endDate?: string | null,
  userId?: string
) =>
  useQuery<AttendanceApiResponse, AxiosError>(
    ["table-attendance", page, limit, filterType, startDate, endDate, userId],
    () => getAttendanceEmployee(page, limit, filterType, startDate, endDate, userId),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Attendance Table",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load attendance table",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    }
  );
